/**
 * useElectron Hook
 * Electron 환경과 React 앱 간의 통합을 위한 훅
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Electron API 사용 여부 확인
 * @returns {boolean} Electron 환경 여부
 */
export const isElectronEnvironment = () => {
  return typeof window !== 'undefined' &&
         window.electronAPI !== undefined &&
         window.electronAPI.isElectron === true;
};

/**
 * Electron 통합 기능을 제공하는 React 훅
 *
 * @returns {Object} Electron 관련 상태 및 메서드
 *
 * @example
 * const {
 *   isElectron,
 *   saveModel,
 *   appInfo,
 *   currentFilePath
 * } = useElectron();
 *
 * // 모델 저장
 * const handleSave = async () => {
 *   if (isElectron) {
 *     const result = await saveModel();
 *     if (result.success) {
 *       console.log('저장됨:', result.filePath);
 *     }
 *   }
 * };
 */
const useElectron = () => {
  // Redux에서 현재 모델 데이터 가져오기
  const modelInfo = useSelector((state) => state.base.common.modelInfo || {});
  const nodes = modelInfo.nodes || [];
  const edges = modelInfo.edges || [];
  const attrs = modelInfo.attrs || [];

  // 상태 관리
  const [currentFilePath, setCurrentFilePath] = useState(null);
  const [appInfo, setAppInfo] = useState(null);
  const [userDataPath, setUserDataPath] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Electron 환경 여부
   */
  const isElectron = useMemo(() => isElectronEnvironment(), []);

  /**
   * Electron API 접근
   */
  const electronAPI = useMemo(() => {
    if (isElectron) {
      return window.electronAPI;
    }
    return null;
  }, [isElectron]);

  /**
   * 앱 정보 로드
   */
  useEffect(() => {
    const loadAppInfo = async () => {
      if (electronAPI) {
        try {
          const info = await electronAPI.getAppInfo();
          setAppInfo(info);

          const dataPath = await electronAPI.getUserDataPath();
          setUserDataPath(dataPath);
        } catch (error) {
          console.error('앱 정보 로드 실패:', error);
        }
      }
    };

    loadAppInfo();
  }, [electronAPI]);

  // Note: 파일 열기 이벤트 핸들러는 ElectronBridge 컴포넌트에서 처리합니다

  /**
   * 현재 모델 데이터 가져오기
   */
  const getModelData = useCallback(() => {
    return {
      nodes,
      edges,
      attrs,
      modelInfo,
      version: '1.0',
      savedAt: new Date().toISOString(),
    };
  }, [nodes, edges, attrs, modelInfo]);

  /**
   * 모델 저장 (다른 이름으로 저장)
   * @param {string} fileName - 기본 파일명
   * @returns {Promise<Object>} 저장 결과
   */
  const saveModel = useCallback(async (fileName) => {
    if (!electronAPI) {
      return { success: false, error: 'Electron 환경이 아닙니다.' };
    }

    setIsLoading(true);
    try {
      const modelData = getModelData();
      const content = JSON.stringify(modelData, null, 2);
      const defaultPath = fileName || currentFilePath || `${modelInfo.mdlrmdlNm || 'model'}.json`;

      const result = await electronAPI.saveFile({
        defaultPath,
        content,
      });

      if (result.success) {
        setCurrentFilePath(result.filePath);
      }

      return result;
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [electronAPI, getModelData, currentFilePath, modelInfo]);

  /**
   * JSON 내보내기
   * @param {string} fileName - 기본 파일명
   * @returns {Promise<Object>} 내보내기 결과
   */
  const exportJson = useCallback(async (fileName) => {
    if (!electronAPI) {
      return { success: false, error: 'Electron 환경이 아닙니다.' };
    }

    setIsLoading(true);
    try {
      const modelData = getModelData();
      const content = JSON.stringify(modelData, null, 2);
      const defaultPath = fileName || `${modelInfo.mdlrmdlNm || 'export'}.json`;

      const result = await electronAPI.exportJson({
        defaultPath,
        content,
      });

      return result;
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [electronAPI, getModelData, modelInfo]);

  /**
   * DDL 내보내기
   * @param {string} ddlContent - DDL 내용
   * @param {string} fileName - 기본 파일명
   * @returns {Promise<Object>} 내보내기 결과
   */
  const exportDDL = useCallback(async (ddlContent, fileName) => {
    if (!electronAPI) {
      return { success: false, error: 'Electron 환경이 아닙니다.' };
    }

    setIsLoading(true);
    try {
      const defaultPath = fileName || `${modelInfo.mdlrmdlNm || 'ddl'}.sql`;

      const result = await electronAPI.exportDDL({
        defaultPath,
        content: ddlContent,
      });

      return result;
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [electronAPI, modelInfo]);

  /**
   * 클립보드에 복사
   * @param {string} text - 복사할 텍스트
   * @returns {Promise<boolean>} 성공 여부
   */
  const copyToClipboard = useCallback(async (text) => {
    if (electronAPI) {
      return electronAPI.copyToClipboard(text);
    }
    // 웹 환경 폴백
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }, [electronAPI]);

  /**
   * 클립보드에서 읽기
   * @returns {Promise<string|null>} 클립보드 내용
   */
  const readFromClipboard = useCallback(async () => {
    if (electronAPI) {
      return electronAPI.readFromClipboard();
    }
    // 웹 환경 폴백
    try {
      return await navigator.clipboard.readText();
    } catch {
      return null;
    }
  }, [electronAPI]);

  /**
   * 외부 링크 열기
   * @param {string} url - URL
   */
  const openExternal = useCallback((url) => {
    if (electronAPI) {
      electronAPI.openExternal(url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [electronAPI]);

  /**
   * 메뉴 액션 리스너 등록
   * @param {Function} callback - 콜백 함수
   * @returns {Function} 구독 해제 함수
   */
  const onMenuAction = useCallback((callback) => {
    const handler = (event) => {
      callback(event.detail.action);
    };
    window.addEventListener('electron-menu-action', handler);
    return () => window.removeEventListener('electron-menu-action', handler);
  }, []);

  /**
   * 저장 요청 리스너 등록
   * @param {Function} callback - 콜백 함수
   * @returns {Function} 구독 해제 함수
   */
  const onSaveRequest = useCallback((callback) => {
    window.addEventListener('electron-save-request', callback);
    return () => window.removeEventListener('electron-save-request', callback);
  }, []);

  /**
   * 내보내기 요청 리스너 등록
   * @param {Function} callback - 콜백 함수
   * @returns {Function} 구독 해제 함수
   */
  const onExportRequest = useCallback((callback) => {
    window.addEventListener('electron-export-request', callback);
    return () => window.removeEventListener('electron-export-request', callback);
  }, []);

  return {
    // 환경 정보
    isElectron,
    appInfo,
    userDataPath,

    // 상태
    currentFilePath,
    isLoading,

    // 파일 작업
    saveModel,
    exportJson,
    exportDDL,
    getModelData,

    // 클립보드
    copyToClipboard,
    readFromClipboard,

    // 외부 링크
    openExternal,

    // 이벤트 리스너
    onMenuAction,
    onSaveRequest,
    onExportRequest,
  };
};

export default useElectron;
export { useElectron };
